import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Box, Typography, Paper, TextField, Button, Alert,
  Divider, Checkbox, FormControlLabel, CircularProgress, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReactMarkdown from 'react-markdown';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../../auth/AuthContext';
import { getLegalStatus, signBaa, getBaaRecord } from '../../api/developerApi';
import ApiErrorAlert from '../../components/ApiErrorAlert';
import { BAA_CONTENT } from '../../assets/legalContent';

export default function SettingsPage() {
  const { user, changePassword } = useAuth();
  const queryClient = useQueryClient();
  const [pwSuccess, setPwSuccess] = useState(false);
  const [pwError, setPwError] = useState('');
  const [baaAgreed, setBaaAgreed] = useState(false);
  const [baaError, setBaaError] = useState('');
  const [viewOpen, setViewOpen] = useState(false);
  const { register, handleSubmit, reset, formState: { isSubmitting } } = useForm();

  const { data: legalStatus, error: legalError } = useQuery('legal-status', getLegalStatus, { staleTime: 60 * 1000 });
  const { data: baaRecord, error: baaRecordError } = useQuery('baa-record', getBaaRecord, {
    enabled: !!legalStatus?.baaAccepted,
    staleTime: Infinity,
  });

  const { mutate: doSignBaa, isLoading: signingBaa } = useMutation(signBaa, {
    onSuccess: () => {
      queryClient.invalidateQueries('legal-status');
      queryClient.invalidateQueries('baa-record');
      setBaaAgreed(false);
    },
    onError: (e) => setBaaError(e?.response?.data?.message || 'Signing failed. Please try again.'),
  });

  const onPasswordSubmit = async ({ oldPassword, newPassword }) => {
    setPwError(''); setPwSuccess(false);
    try {
      await changePassword(oldPassword, newPassword);
      setPwSuccess(true);
      reset();
    } catch (e) {
      setPwError(e.message || 'Password change failed.');
    }
  };

  const baaAvailable = legalStatus?.baaAvailable;
  const baaAccepted = legalStatus?.baaAccepted;
  const baaSignedAt = legalStatus?.baaSignedAt
    ? new Date(legalStatus.baaSignedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={0.5}>Settings</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>Account preferences</Typography>

      <ApiErrorAlert error={legalError || baaRecordError} sx={{ mb: 2 }} />

      {/* Account info */}
      <Paper elevation={0} sx={{ p: 3, border: '1px solid #E4E4E7', borderRadius: 2, maxWidth: 480, mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} mb={2}>Account</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Name</Typography>
            <Typography variant="body2">{user?.firstName} {user?.lastName}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Email</Typography>
            <Typography variant="body2">{user?.email}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Partner ID</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{user?.partnerId || '—'}</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Change password */}
      <Paper elevation={0} sx={{ p: 3, border: '1px solid #E4E4E7', borderRadius: 2, maxWidth: 480, mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} mb={2}>Change password</Typography>
        <Divider sx={{ mb: 2 }} />
        {pwSuccess && <Alert severity="success" sx={{ mb: 2 }}>Password updated.</Alert>}
        {pwError && <Alert severity="error" sx={{ mb: 2 }}>{pwError}</Alert>}
        <Box component="form" onSubmit={handleSubmit(onPasswordSubmit)} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Current password" type="password" size="small" fullWidth
            {...register('oldPassword', { required: true })} />
          <TextField label="New password" type="password" size="small" fullWidth
            {...register('newPassword', { required: true, minLength: 8 })}
            helperText="Minimum 8 characters" />
          <Button type="submit" variant="contained" disabled={isSubmitting} sx={{
            alignSelf: 'flex-start', bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' },
            textTransform: 'none', fontWeight: 600,
          }}>
            {isSubmitting ? 'Updating…' : 'Update password'}
          </Button>
        </Box>
      </Paper>

      {/* HIPAA BAA */}
      <Paper elevation={0} sx={{ p: 3, border: '1px solid #E4E4E7', borderRadius: 2, maxWidth: 720 }}>
        <Typography variant="subtitle2" fontWeight={600} mb={0.5}>HIPAA Business Associate Agreement</Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Required before transmitting Protected Health Information (PHI) via the Vectros API.
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {!baaAvailable && (
          <Alert severity="info" sx={{ mb: 0 }}>
            HIPAA BAA is available on the Pro plan and above.{' '}
            <Link to="/billing" style={{ color: 'inherit', fontWeight: 600 }}>Upgrade to Pro →</Link>
          </Alert>
        )}

        {baaAvailable && baaAccepted && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 20 }} />
              <Typography variant="body2" fontWeight={600}>Signed</Typography>
              <Chip label={`Signed ${baaSignedAt}`} size="small" sx={{ bgcolor: '#f0fdf4', color: '#166534', fontWeight: 500 }} />
            </Box>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Signed by: {baaRecord?.signerName} ({baaRecord?.signerEmail})
            </Typography>
            <Button size="small" variant="outlined" onClick={() => setViewOpen(true)}
              sx={{ textTransform: 'none', borderColor: '#E4E4E7', color: 'text.primary' }}>
              View signed agreement
            </Button>
          </Box>
        )}

        {baaAvailable && !baaAccepted && (
          <Box>
            <Box sx={{
              height: 320, overflowY: 'auto', border: '1px solid #E4E4E7',
              borderRadius: 1, bgcolor: '#FAFAFA', p: 2, mb: 2,
              '& h1,h2,h3': { fontWeight: 700, mt: 2, mb: 1 },
              '& p': { mb: 1.5, lineHeight: 1.7, fontSize: 13 },
              '& ul,ol': { pl: 3, mb: 1.5 },
              '& li': { fontSize: 13, mb: 0.5 },
            }}>
              <ReactMarkdown>{BAA_CONTENT}</ReactMarkdown>
            </Box>

            {baaError && <Alert severity="error" sx={{ mb: 2 }}>{baaError}</Alert>}

            <FormControlLabel
              control={<Checkbox checked={baaAgreed} onChange={(e) => setBaaAgreed(e.target.checked)} />}
              label={
                <Typography variant="body2">
                  I am authorized to execute this agreement on behalf of <strong>{user?.firstName} {user?.lastName}</strong> and agree to its terms
                </Typography>
              }
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained"
              disabled={!baaAgreed || signingBaa}
              onClick={() => doSignBaa()}
              sx={{ bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' }, fontWeight: 600, textTransform: 'none' }}
            >
              {signingBaa ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Sign Business Associate Agreement'}
            </Button>
          </Box>
        )}
      </Paper>

      {/* View signed BAA modal */}
      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Signed Business Associate Agreement</DialogTitle>
        <DialogContent dividers sx={{
          '& h1,h2,h3': { fontWeight: 700, mt: 2, mb: 1 },
          '& p': { mb: 1.5, lineHeight: 1.7, fontSize: 13 },
          '& ul,ol': { pl: 3, mb: 1.5 },
          '& li': { fontSize: 13, mb: 0.5 },
        }}>
          {baaRecord?.signedContent
            ? <ReactMarkdown>{baaRecord.signedContent}</ReactMarkdown>
            : <CircularProgress size={24} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
